/*
 * Progressive navigation and consent-aware custom events. No cart mutations.
 * Event contract (all payloads also include edition; no customer data is read):
 * - gift_guide_view: placement
 * - gift_guide_invitation_select: placement
 * - gift_guide_direct_anchor_arrival: target_type, chapter or product_id/chapter/position
 * - gift_guide_chapter_exposure: chapter
 * - gift_guide_product_exposure: product_id, chapter, position
 * - gift_guide_product_select: product_id, chapter, position, action_type
 * - gift_guide_personalization_intent: product_id, chapter, position
 * - gift_guide_exit_select: destination, optional chapter
 * - gift_guide_chapter_select: chapter, placement
 * - gift_guide_pdf_download: placement
 */
(() => {
  'use strict';
  if (window.ccGiftCatalogInstalled) return;
  window.ccGiftCatalogInstalled = true;
  const edition = '2026';
  const cardExposureThreshold = 0.25;
  const chapterExposureThreshold = 0.5;
  const exitActions = new Set(['collection', 'contact', 'shipping', 'returns', 'engraving', 'hub']);
  const isPreview = () => Boolean(window.Shopify?.designMode || (window.Shopify?.theme?.role && window.Shopify.theme.role !== 'main') || new URL(location.href).searchParams.has('preview_theme_id'));
  const analyticsAllowed = () => {
    try { return window.Shopify?.customerPrivacy?.analyticsProcessingAllowed?.() === true; }
    catch (_) { return false; }
  };
  const publish = (name, properties = {}) => {
    if (isPreview() || !analyticsAllowed()) return false;
    if (typeof window.Shopify?.analytics?.publish !== 'function') return false;
    try {
      const result = window.Shopify.analytics.publish(name, { edition, ...properties });
      if (result && typeof result.catch === 'function') result.catch(() => {});
      return true;
    } catch (_) {
      // Shopping and navigation must remain independent of analytics.
      return false;
    }
  };
  let viewSent = false;
  const view = () => {
    if (!document.querySelector('[data-gift-edition]') || viewSent) return;
    if (publish('gift_guide_view', { placement: new URL(location.href).searchParams.get('guide_source') === 'gift-center' ? 'gift-center' : 'direct-or-external' })) viewSent = true;
  };

  let directAnchorSent = false;
  const directAnchorArrival = () => {
    if (directAnchorSent || !location.hash) return;
    let targetId;
    try { targetId = decodeURIComponent(location.hash.slice(1)); }
    catch (_) { return; }
    const target = document.getElementById?.(targetId);
    if (!target || !target.closest?.('.cc-gift')) return;
    let properties;
    if (target.dataset?.guideChapter) {
      properties = { target_type: 'chapter', chapter: target.dataset.guideChapter };
    } else if (target.dataset?.guidePick) {
      properties = { target_type: 'product', ...productContext(target) };
    } else {
      return;
    }
    if (publish('gift_guide_direct_anchor_arrival', properties)) directAnchorSent = true;
  };

  const visibleTargets = new Set();
  const observedTargets = new WeakSet();
  const exposedTargets = new WeakSet();
  const productContext = target => {
    const link = target.querySelector?.('[data-guide-action="product"], [data-guide-action="personalize"]');
    return {
      product_id: target.dataset?.guidePick,
      chapter: link?.dataset?.chapter,
      position: Number(link?.dataset?.position)
    };
  };
  const flushExposures = () => {
    for (const target of visibleTargets) {
      if (exposedTargets.has(target)) continue;
      let sent = false;
      if (target.dataset?.guidePick) {
        sent = publish('gift_guide_product_exposure', productContext(target));
      } else {
        const chapter = target.closest?.('[data-guide-chapter]')?.dataset?.guideChapter;
        if (chapter) sent = publish('gift_guide_chapter_exposure', { chapter });
      }
      if (sent) exposedTargets.add(target);
    }
  };
  const exposureObserver = typeof window.IntersectionObserver === 'function' ? new window.IntersectionObserver(entries => {
    for (const entry of entries) {
      const threshold = entry.target.dataset?.guidePick ? cardExposureThreshold : chapterExposureThreshold;
      if (entry.isIntersecting && entry.intersectionRatio >= threshold) visibleTargets.add(entry.target);
      else visibleTargets.delete(entry.target);
    }
    flushExposures();
  }, { threshold: [0, cardExposureThreshold, chapterExposureThreshold] }) : null;
  const observeExposures = () => {
    if (!exposureObserver) return;
    for (const target of document.querySelectorAll?.('[data-guide-chapter] .cc-gift-chapter__heading, [data-guide-pick]') || []) {
      if (observedTargets.has(target)) continue;
      observedTargets.add(target);
      exposureObserver.observe(target);
    }
  };

  const activate = () => {
    view();
    directAnchorArrival();
    observeExposures();
    flushExposures();
  };
  activate();
  document.addEventListener('visitorConsentCollected', activate);
  document.addEventListener('shopify:section:load', activate);
  document.addEventListener('click', event => {
    const link = event.target?.closest?.('a');
    if (!link || !link.closest('.cc-gift')) return;
    const action = link.dataset.guideAction;
    const personalizationIntent = action === 'personalize' || link.dataset.guidePersonalization === 'true';
    if (action === 'product' || personalizationIntent) {
      const properties = { product_id: link.dataset.productId, chapter: link.dataset.chapter, position: Number(link.dataset.position) };
      publish('gift_guide_product_select', { ...properties, action_type: personalizationIntent ? 'personalize' : 'product-page' });
      if (personalizationIntent) publish('gift_guide_personalization_intent', properties);
      const pick = link.closest('[data-guide-pick]');
      // Keep a useful return anchor without intercepting the link or creating a new history entry.
      if (pick?.id && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey && !event.defaultPrevented && event.button === 0 && (!link.target || link.target === '_self')) {
        try { history.replaceState(history.state, '', `${location.pathname}${location.search}#${encodeURIComponent(pick.id)}`); } catch (_) {}
      }
    } else if (action === 'invitation') {
      publish('gift_guide_invitation_select', { placement: 'gift-center' });
    } else if (exitActions.has(action)) {
      const chapter = link.closest('[data-guide-chapter]')?.dataset?.guideChapter;
      const properties = { destination: action };
      if (chapter) properties.chapter = chapter;
      publish('gift_guide_exit_select', properties);
    } else if (action === 'pdf') {
      publish('gift_guide_pdf_download', { placement: 'guide-close' });
    } else if (link.hash && link.origin === location.origin && link.pathname === location.pathname) {
      let targetId;
      try { targetId = decodeURIComponent(link.hash.slice(1)); }
      catch (_) { return; }
      const target = document.getElementById?.(targetId);
      const chapter = link.dataset.chapter || target?.dataset?.guideChapter;
      if (target?.closest?.('.cc-gift') && chapter) {
        publish('gift_guide_chapter_select', { chapter, placement: link.closest('.cc-gift-contents') ? 'contents' : 'chapter' });
      }
    }
  });
})();
